import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Upload } from "lucide-react";

const EditResearch = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get the research ID from URL params
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: "",
    area: "",
    description: "",
    content: "",
    thumbnail: null,
    thumbnailPreview: null,
    status: "draft",
  });

  const [errors, setErrors] = useState({});

  // Fetch research data on component mount
  useEffect(() => {
    const fetchResearch = async () => {
      setIsLoading(true);
      try {
        // In a real app, this would be an API call
        // Example: const response = await api.get(`/research/${id}`);

        // For now, let's simulate an API response
        await new Promise((resolve) => setTimeout(resolve, 800));

        // Mock data - in a real app, this would come from the API
        const researchData = {
          id: id,
          title: "Example Research Title",
          area: "minerals",
          description:
            "This is an example research description that would be loaded from the API.",
          content:
            "Detailed content of the research paper would be loaded here. This is just placeholder text for the example.",
          status: "active",
          thumbnailUrl: "https://example.com/image.jpg", // In a real app, this would be the image URL
        };

        setFormData({
          ...researchData,
          thumbnail: null, // We don't actually receive the file, just the URL
          thumbnailPreview: researchData.thumbnailUrl, // Set the preview from the URL
        });
      } catch (error) {
        console.error("Error fetching research:", error);
        setErrors({
          form: "Failed to load research data. Please try again.",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchResearch();
  }, [id]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null,
      });
    }
  };

  // Handle rich text editor content change
  const handleContentChange = (content) => {
    setFormData({
      ...formData,
      content,
    });

    // Clear error for this field
    if (errors.content) {
      setErrors({
        ...errors,
        content: null,
      });
    }
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({
        ...formData,
        thumbnail: file,
        thumbnailPreview: URL.createObjectURL(file),
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.area) {
      newErrors.area = "Research area is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.content.trim()) {
      newErrors.content = "Content is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Here you would make an API call to update the research
      // For example: await api.put(`/research/${id}`, formData);

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // After successful submission, navigate back to research list
      navigate("/admin/research");
    } catch (error) {
      console.error("Error updating research:", error);
      setErrors({
        ...errors,
        form: "There was an error updating the research. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center mb-6">
          <Link to="/admin/research" className="mr-4">
            <button className="p-2 rounded-full hover:bg-gray-100">
              <ArrowLeft size={20} />
            </button>
          </Link>
          <h1 className="text-2xl font-bold">Edit Research</h1>
        </div>
        <div className="flex justify-center items-center h-64">
          <p className="text-gray-500">Loading research data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center mb-6">
        <Link to="/admin/research" className="mr-4">
          <button className="p-2 rounded-full hover:bg-gray-100">
            <ArrowLeft size={20} />
          </button>
        </Link>
        <h1 className="text-2xl font-bold">Edit Research</h1>
      </div>

      {errors.form && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded">
          {errors.form}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content Column */}
          <div className="md:col-span-2 space-y-6">
            <div>
              <label htmlFor="title" className="block mb-2 font-medium">
                Research Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter research title"
                className={`w-full p-3 border rounded ${
                  errors.title ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.title && (
                <p className="mt-1 text-red-500 text-sm">{errors.title}</p>
              )}
            </div>

            <div>
              <label htmlFor="description" className="block mb-2 font-medium">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                placeholder="Brief description of the research"
                className={`w-full p-3 border rounded resize-y ${
                  errors.description ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.description && (
                <p className="mt-1 text-red-500 text-sm">
                  {errors.description}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="content" className="block mb-2 font-medium">
                Research Content <span className="text-red-500">*</span>
              </label>
              {/* This would typically be a rich text editor component */}
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={(e) => handleContentChange(e.target.value)}
                rows={10}
                placeholder="Detailed research content"
                className={`w-full p-3 border rounded resize-y ${
                  errors.content ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.content && (
                <p className="mt-1 text-red-500 text-sm">{errors.content}</p>
              )}
            </div>
          </div>

          {/* Settings Column */}
          <div className="space-y-6">
            <div className="border rounded p-4 bg-gray-50">
              <h3 className="font-medium mb-4">Research Settings</h3>

              <div className="mb-4">
                <label htmlFor="area" className="block mb-2">
                  Research Area <span className="text-red-500">*</span>
                </label>
                <select
                  id="area"
                  name="area"
                  value={formData.area}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded ${
                    errors.area ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">Select an area</option>
                  <option value="minerals">Mineral Research</option>
                  <option value="cathodic">Cathodic Protection</option>
                  <option value="corrosion">Corrosion Science</option>
                </select>
                {errors.area && (
                  <p className="mt-1 text-red-500 text-sm">{errors.area}</p>
                )}
              </div>

              <div className="mb-4">
                <label htmlFor="status" className="block mb-2">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full p-3 border border-gray-300 rounded"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div>
                <label htmlFor="thumbnail" className="block mb-2">
                  Featured Image
                </label>
                <div
                  className={`border-2 border-dashed rounded-lg p-4 text-center ${
                    formData.thumbnailPreview
                      ? ""
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  {formData.thumbnailPreview ? (
                    <div className="relative">
                      <img
                        src={formData.thumbnailPreview}
                        alt="Thumbnail preview"
                        className="max-h-48 mx-auto rounded"
                      />
                      <button
                        type="button"
                        className="absolute top-2 right-2 bg-white rounded-full p-1 shadow"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            thumbnail: null,
                            thumbnailPreview: null,
                          })
                        }
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="thumbnail-input"
                      className="cursor-pointer flex flex-col items-center py-4"
                    >
                      <Upload size={32} className="text-gray-400 mb-2" />
                      <span className="text-sm text-gray-500">
                        Click to upload image
                      </span>
                      <span className="text-xs text-gray-400 mt-1">
                        JPG, PNG, GIF up to 5MB
                      </span>
                    </label>
                  )}
                  <input
                    type="file"
                    id="thumbnail-input"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <Link to="/admin/research">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </Link>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center disabled:opacity-50"
              >
                {loading ? (
                  "Saving..."
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    Update Research
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditResearch;
